/* Status and Legal review actions on the contract page, shared by the classic
 * page (pages/legal/LegalContractDetail.js) and the new look one
 * (newlook/pages/legalcontract/LegalContractPage.js).
 *
 * Each action's response is the full contract detail payload, so the page
 * swaps it in with ``onUpdated``. ``onMessage({ kind, message })`` is the
 * page's own flash. What a user may do comes from the server's flags on the
 * contract (``can_change_status``, ``can_review``) and is re-checked there.
 */
import { useState } from 'react';

import legalContractService, { legalRefusal } from '../../services/legalContractService';

export default function useContractReview({ contract, onUpdated, onMessage }) {
  // 'status' | 'send' | 'review' while a request runs.
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState('');

  const act = async (key, call, success, fallback) => {
    setBusy(key);
    try {
      const data = await call();
      onUpdated(data);
      onMessage({ kind: 'success', message: success(data) });
      return true;
    } catch (err) {
      onMessage({ kind: 'error', message: legalRefusal(err, fallback) });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const changeStatus = (status) => act(
    'status',
    () => legalContractService.setStatus(contract.id, status),
    (data) => `Contract status is now ${data.status_display}.`,
    'Could not change the contract status. Please try again.',
  );

  const sendToLegal = () => act(
    'send',
    () => legalContractService.sendToLegal(contract.id),
    () => 'Sent to Legal for review. Legal has been notified.',
    'Could not send the contract to Legal. Please try again.',
  );

  const markReviewed = async () => {
    const done = await act(
      'review',
      () => legalContractService.markReviewed(contract.id, note),
      () => 'Marked as reviewed. The person who sent it has been told.',
      'Could not mark the contract reviewed. Please try again.',
    );
    if (done) setNote('');
  };

  return { busy, note, setNote, changeStatus, sendToLegal, markReviewed };
}

// "Lena Legal, 17 Oct 2026 14:05" for the sent / reviewed lines.
export const byLine = (name, when) => {
  if (!when) return '';
  let date = when;
  try {
    date = new Date(when).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_e) { /* keep the raw value */ }
  return name ? `${name}, ${date}` : date;
};
